import type { ClockSchedule } from '@felix/domain';
import {
  type PmsReportBasis,
  type PmsReportCell,
  type PmsReportCellKind,
  type PmsReportColumn,
  type PmsReportGroup,
  type PmsReportId,
  PRODUCT_NAME,
} from '@felix/domain';
import { formatDateTime } from '../../format.js';

/**
 * NR496 raporunun dilden bağımsız verisinden basılacak belge (plan M7 Task 29): başlıklar, sütun
 * adları, kodların ve işaretlerin metni dilden gelir. PDF (`report-pdf.ts`, istemcide), Excel ve
 * CSV (`@felix/api-core` yazarları, sunucuda) aynı belgeyi basar. React ve Mantine içermez:
 * gemi sunucusu ve bulut da kullanır (`@felix/ui/pms-report-export`).
 */

/** Gemi sunucusunun ve bulutun döndürdüğü rapor verisi (`@felix/db` `PmsReportData`). */
export interface PmsReportView {
  id: PmsReportId;
  group: PmsReportGroup;
  basis: PmsReportBasis;
  sources: readonly string[];
  rule: string;
  vessel: {
    id: string;
    name: string;
    imo: string | null;
    timeZone: string;
    /** Gemi saati çizelgesi (ADR 0065 §4); eski sunucu göndermez. */
    clock?: ClockSchedule;
  };
  from: string | null;
  to: string | null;
  asOf: string;
  equipment: { id: string; code: string; name: string } | null;
  /** Sertifika kategorisi süzgeci (Vessel Certificates). */
  category?: string | null;
  /** Rapora özgü süzgeçler (`@felix/domain` `PmsReportFilters`). */
  filters?: Readonly<Record<string, string>>;
  notes: readonly string[];
  tables: readonly {
    key: string;
    columns: readonly PmsReportColumn[];
    rows: readonly (readonly PmsReportCell[])[];
    marks: readonly (string | null)[];
  }[];
  summary: readonly { key: string; value: number | string | null }[];
  hash: string;
  generatedAt: string;
}

/** Basım bilgisi [NR496 4.1.2]. */
export interface ReportPrint {
  /** Yazılımın sürümü (`Felixmate` adıyla basılır). */
  version: string;
  /** Basım anı, ISO-8601 UTC. */
  printedAt: string;
  printedBy: { name: string; role: string | null };
  /** Basım anının gemi saati farkı (dakika). */
  tzOffsetMin: number;
  /** Şirket markası (ADR 0019 §4). */
  brand: string | null;
  /** Sayı biçimi dili. */
  locale: string;
}

/** Metin fonksiyonu: i18next `t` ya da `pmsReportText`. Bulunamayan anahtar kendisi döner. */
export type ReportT = (key: string, options?: Record<string, unknown>) => string;

export type ReportDocumentCell = string | number | null;

export interface ReportDocumentTable {
  key: string;
  title: string;
  columns: { key: string; label: string; kind: PmsReportCellKind }[];
  /** Kod ve evet/hayır çevrilmiş; gün `YYYY-MM-DD`, an ISO-8601 UTC, sayı sayı. */
  rows: ReportDocumentCell[][];
  /** Satırın işareti (`void`, `reapproval_pending`, `deleted`). */
  marks: (string | null)[];
  markLabels: (string | null)[];
}

export interface ReportDocument {
  id: PmsReportId;
  title: string;
  group: string;
  brand: string | null;
  product: string;
  version: string;
  vessel: string;
  from: string | null;
  to: string | null;
  asOf: string;
  equipment: string | null;
  /** Uygulanan süzgeçlerin metni ("Class: Yes; Critical: Yes"); yoksa boş. */
  filters: string | null;
  basis: string;
  sources: string;
  rule: string;
  notes: string[];
  printed: { at: string; tzOffsetMin: number; by: string };
  hash: string;
  locale: string;
  tables: ReportDocumentTable[];
  summary: { label: string; value: string }[];
  /** Excel ve CSV (`@felix/api-core` `TableExportDocument`). */
  headline: string[];
  about: { label: string; value: string }[];
  markLabel: string;
  empty: string;
  labels: { period: string; source: string; rule: string; summary: string; filters: string };
  /** Sayfa metninin kalıbı: `%PAGE%` ve `%PAGES%` basımda doldurulur. */
  pageTemplate: string;
  /** Altlığın metni (ekran biçimiyle; sayfa numarası hariç) [NR496 4.1.2]. */
  footer: string;
}

/** Çeviri; anahtar bulunamazsa `fallback`. */
function translate(t: ReportT, key: string, fallback: string): string {
  const value = t(key);
  return value === key || value === '' ? fallback : value;
}

/** `YYYY-MM-DD HH:mm` (UTC). */
export function utcMinute(at: string): string {
  return new Date(Date.parse(at)).toISOString().slice(0, 16).replace('T', ' ');
}

/** Saat farkı `UTC±hh:mm`. */
function offsetLabel(tzOffsetMin: number): string {
  const abs = Math.abs(tzOffsetMin);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  return `UTC${tzOffsetMin < 0 ? '-' : '+'}${hours}:${String(abs % 60).padStart(2, '0')}`;
}

/** Gemi saati: `YYYY-MM-DD HH:mm (UTC±hh:mm)`. */
export function shipMinute(at: string, tzOffsetMin: number): string {
  const local = new Date(Date.parse(at) + tzOffsetMin * 60_000).toISOString();
  return `${local.slice(0, 10)} ${local.slice(11, 16)} (${offsetLabel(tzOffsetMin)})`;
}

function cellOf(
  t: ReportT,
  column: PmsReportColumn,
  value: PmsReportCell | undefined,
): ReportDocumentCell {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return t(`pmsReports.${value ? 'yes' : 'no'}`);
  if (column.kind === 'code' && typeof value === 'string') {
    // Değişikliğin alanı denetim kaydının alan etiketinden (K37)
    if (column.key === 'field') return translate(t, `pmsAudit.fields.${value}`, value);
    return translate(t, `pmsReports.codes.${value}`, value);
  }
  return value;
}

/** Rapor verisinden basılacak belge. */
export function pmsReportDocument(
  data: PmsReportView,
  print: ReportPrint,
  t: ReportT,
): ReportDocument {
  const title = t(`pmsReports.reports.${data.id}.title`);
  const vessel = data.vessel.imo
    ? `${data.vessel.name} (IMO ${data.vessel.imo})`
    : data.vessel.name;
  const product = PRODUCT_NAME;
  const by = print.printedBy.role
    ? `${print.printedBy.name} (${print.printedBy.role})`
    : print.printedBy.name;
  const period =
    data.from && data.to
      ? data.from === data.to
        ? data.from
        : `${data.from} – ${data.to}`
      : `${t('pmsReports.export.asOf')} ${data.asOf}`;
  const basis = t(`pmsReports.basis.${data.basis}`);
  const filterEntries = Object.entries(data.filters ?? {});
  const filters =
    filterEntries.length === 0
      ? null
      : filterEntries
          .map(
            ([key, value]) =>
              `${t(`pmsReports.filters.${key}`)}: ${translate(t, `pmsReports.filters.${value}`, value)}`,
          )
          .join('; ');
  const notes = data.notes.map((note) => t(`pmsReports.notes.${note}`));
  const markLabel = t('pmsReports.export.mark');
  const tables = data.tables.map(
    (table): ReportDocumentTable => ({
      key: table.key,
      title: t(`pmsReports.tables.${table.key}`),
      columns: table.columns.map((column) => ({
        key: column.key,
        label: t(`pmsReports.columns.${column.key}`),
        kind: column.kind,
      })),
      rows: table.rows.map((row) => table.columns.map((column, i) => cellOf(t, column, row[i]))),
      marks: [...table.marks],
      markLabels: table.marks.map((mark) => (mark ? t(`pmsReports.marks.${mark}`) : null)),
    }),
  );
  const summary = data.summary.map((item) => ({
    label: translate(t, `pmsReports.summary.${item.key}`, item.key),
    value:
      item.value === null
        ? '—'
        : typeof item.value === 'number'
          ? item.value.toLocaleString(print.locale, { maximumFractionDigits: 1 })
          : item.value,
  }));
  const printedUtc = `${utcMinute(print.printedAt)} UTC`;
  const printedShip = shipMinute(print.printedAt, print.tzOffsetMin);
  const software = `${product} v${print.version}`;
  return {
    id: data.id,
    title,
    group: t(`pmsReports.groups.${data.group}`),
    brand: print.brand,
    product,
    version: print.version,
    vessel,
    from: data.from,
    to: data.to,
    asOf: data.asOf,
    equipment: data.equipment ? `${data.equipment.code} ${data.equipment.name}` : null,
    filters,
    basis,
    sources: data.sources.join(', '),
    rule: data.rule,
    notes,
    printed: { at: print.printedAt, tzOffsetMin: print.tzOffsetMin, by },
    hash: data.hash,
    locale: print.locale,
    tables,
    summary,
    headline: [software, title, vessel, ...(filters ? [filters] : []), printedUtc, by, data.hash],
    about: [
      { label: t('pmsReports.export.software'), value: software },
      { label: t('pmsReports.export.report'), value: title },
      { label: t('pmsReports.export.group'), value: t(`pmsReports.groups.${data.group}`) },
      { label: t('pmsReports.export.vessel'), value: vessel },
      { label: t('pmsReports.export.period'), value: period },
      ...(data.equipment
        ? [
            {
              label: t('pmsReports.export.equipment'),
              value: `${data.equipment.code} ${data.equipment.name}`,
            },
          ]
        : []),
      ...(data.category
        ? [
            {
              label: t('pmsReports.columns.category'),
              value: translate(t, `pmsReports.codes.${data.category}`, data.category),
            },
          ]
        : []),
      ...(filters ? [{ label: t('pmsReports.export.filters'), value: filters }] : []),
      { label: t('pmsReports.export.source'), value: basis },
      { label: t('pmsReports.export.tables'), value: data.sources.join(', ') },
      { label: t('pmsReports.export.rule'), value: data.rule },
      { label: t('pmsReports.export.printedAt'), value: printedUtc },
      { label: t('pmsReports.export.printedAtShip'), value: printedShip },
      { label: t('pmsReports.export.printedBy'), value: by },
      { label: t('pmsReports.export.hash'), value: data.hash },
    ],
    markLabel,
    empty: t('pmsReports.export.empty'),
    labels: {
      period: t('pmsReports.export.period'),
      source: t('pmsReports.export.source'),
      rule: t('pmsReports.export.rule'),
      summary: t('pmsReports.export.summary'),
      filters: t('pmsReports.export.filters'),
    },
    pageTemplate: t('pmsReports.export.page', { page: '%PAGE%', pages: '%PAGES%' }),
    footer: t('pmsReports.export.footer', {
      product,
      version: print.version,
      utc: formatDateTime(print.printedAt, 0),
      ship: `${formatDateTime(print.printedAt, print.tzOffsetMin)} (${offsetLabel(print.tzOffsetMin)})`,
      person: by,
    }),
  };
}
